import {
  BoundRadioGroupField,
  Css,
  StyledRadio,
  type StyledRadioProps,
  Tokens,
  useComputed,
  useTestIds,
} from "@homebound/beam";
import type { FieldState } from "@homebound/form-state";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { ConflictIcon } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/ConflictIcon";
import { conflictMessage } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/optionCardUtils";
import type { OptionVariantRows_CheckoutConfigOptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/OptionVariantRows.generated";
import {
  OptionPrice,
  OptionPriceMode,
  optionAmountInCents,
} from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

export type OptionVariantRowsProps = {
  group: OptionVariantRows_CheckoutConfigOptionGroupFragment;
  /** The form field that stores the selected variant's `ReadyPlanOption` id. */
  field: FieldState<string | null | undefined>;
  /** Disables all the variants, e.g. while the personalization option they belong to is unchecked. */
  disabled?: boolean;
  priceMode?: OptionPriceMode;
};

/**
 * Renders one radio row per variant of a package or personalization option, showing the variant's name, price and
 * product thumbnails. Used by both `PackageOptionCard` and `PersonalizationOptionCard`.
 */
export function OptionVariantRows(props: OptionVariantRowsProps) {
  const { group, field, disabled = false, priceMode = OptionPriceMode.Price } = props;
  const readOnly = useComputed(() => field.readOnly, [field]);
  const options = group.options.map((o) => ({
    value: o.option.id,
    label: o.option.name,
    disabled: disableBasedOnPotentialOperation(o.canBeSelected),
    conflict: conflictMessage(o.option.name, o.wouldDeselect),
    priceInCents: optionAmountInCents(o, priceMode),
    images: o.displayProducts.map((p) => ({
      id: p.id,
      alt: p.displayName,
      src: p.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL,
    })),
  }));
  return (
    <BoundRadioGroupField
      field={field}
      label={group.group.name}
      labelStyle="hidden"
      disabled={disabled || readOnly}
      options={options}
      renderOption={(option, radioProps) => (
        <VariantRow {...option} radioProps={radioProps} displayDirection={priceMode === OptionPriceMode.Price} />
      )}
    />
  );
}

type VariantRowProps = {
  label: string;
  /** The "Option Conflict" message, when picking the variant would drop other selected options. */
  conflict: string | undefined;
  priceInCents: number;
  /** Off in cost mode, since a cost is the option's total rather than a delta. */
  displayDirection: boolean;
  images: { id: string; alt: string; src: string }[];
  radioProps: StyledRadioProps;
};

function VariantRow(props: VariantRowProps) {
  const { label, conflict, priceInCents, displayDirection, images, radioProps } = props;
  const { isDisabled = false } = radioProps;
  const tid = useTestIds(props, "variantRow");
  return (
    <label
      css={{
        ...Css.df.fdc.gap2.py2.bb.bc(Tokens.SurfaceSeparator).cursorPointer.ifLastOfType.bn.$,
        ...(isDisabled ? Css.cursorNotAllowed.color(Tokens.TextDisabled).$ : {}),
      }}
      {...tid}
    >
      <span css={Css.df.aic.gap1.$}>
        <StyledRadio {...radioProps} />
        <span css={Css.fg1.df.aic.gap1.mdSb.$} {...tid.name}>
          {label}
          <ConflictIcon message={conflict} />
        </span>
        <OptionPrice priceInCents={priceInCents} displayDirection={displayDirection} />
      </span>
      {images.nonEmpty && (
        <span css={Css.df.gap2.ml3.fww.$}>
          {images.map((img) => (
            <img
              key={img.id}
              src={img.src}
              alt={img.alt}
              loading="lazy"
              css={Css.sqPx(96).p1.br8.ba.bc(Tokens.FieldBorderDefault).objectContain.if(isDisabled).o50.$}
              {...tid.image}
            />
          ))}
        </span>
      )}
    </label>
  );
}
